/**
 * FlowSidebar - List of saved flows with navigation
 */

import { useState } from 'react';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';
import type { Flow } from '@/services/flows';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';

/**
 * Parse ISO date string as local date (not UTC).
 * Avoids timezone issues where new Date(isoString) parses as UTC midnight.
 */
function parseLocalDate(isoString: string): Date {
  const parts = isoString.split('-').map(Number);
  const year = parts[0] ?? 2000;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(year, month - 1, day);
}

interface FlowSidebarProps {
  flows: Flow[];
  selectedFlowId: string | undefined;
  onSelectFlow: (flowId: string) => void;
  onCreateFlow: () => void;
  onDeleteFlow: (flowId: string) => void;
  isLoading?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

/**
 * Format period display for sidebar
 */
function formatFlowPeriod(flow: Flow): string {
  // Use parseLocalDate to avoid UTC timezone shift
  const startDate = parseLocalDate(flow.periodStart);

  switch (flow.periodType) {
    case 'month':
      return startDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    case 'quarter': {
      const quarter = Math.floor(startDate.getMonth() / 3) + 1;
      return `Q${quarter} ${startDate.getFullYear()}`;
    }
    case 'year':
      return startDate.getFullYear().toString();
    case 'ytd':
      return `${startDate.getFullYear()} YTD`;
    case 'custom':
      return 'Custom';
    default:
      return flow.periodStart;
  }
}

export default function FlowSidebar({
  flows,
  selectedFlowId,
  onSelectFlow,
  onCreateFlow,
  onDeleteFlow,
  isLoading = false,
  isCollapsed = false,
  onToggleCollapse,
}: FlowSidebarProps) {
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, flowId: string) => {
    e.stopPropagation();
    setDeleteConfirmId(flowId);
  };

  const handleDeleteConfirm = (flowId: string) => {
    onDeleteFlow(flowId);
    setDeleteConfirmId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  // Collapsed state (mobile or toggled)
  if (isCollapsed) {
    return (
      <div className="w-12 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col items-center py-4">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="p-2 rounded-lg hover:bg-gray-100 mb-4"
          aria-label="Expand sidebar"
        >
          <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <button
          type="button"
          onClick={onCreateFlow}
          className="p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700"
          aria-label="Create new flow"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="w-64 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">My Flows</h2>
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="p-1 rounded hover:bg-gray-100 lg:hidden"
              aria-label="Collapse sidebar"
            >
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        <Button onClick={onCreateFlow} className="w-full" size="sm">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Flow
        </Button>
      </div>

      {/* Flow list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        ) : flows.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            <p>No flows created yet.</p>
            <p className="mt-1">Create your first flow to get started.</p>
          </div>
        ) : (
          <ul className="py-2" role="listbox" aria-label="Saved flows">
            {flows.map((flow) => (
              <li key={flow.id} role="option" aria-selected={flow.id === selectedFlowId}>
                {deleteConfirmId === flow.id ? (
                  // Delete confirmation
                  <div className="px-4 py-3 bg-red-50 border-y border-red-100">
                    <p className="text-sm text-red-800 mb-2">Delete this flow?</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleDeleteCancel}
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteConfirm(flow.id)}
                        className="flex-1 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onSelectFlow(flow.id)}
                    className={cn(
                      'w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors',
                      'flex items-start justify-between group',
                      flow.id === selectedFlowId && 'bg-primary-50 border-l-2 border-primary-600'
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'font-medium truncate',
                          flow.id === selectedFlowId ? 'text-primary-900' : 'text-gray-900'
                        )}
                      >
                        {flow.name || formatFlowPeriod(flow)}
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {formatFlowPeriod(flow)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {flow.status === 'processing' ? (
                          <span className="text-blue-600">Computing...</span>
                        ) : flow.status === 'error' ? (
                          <span className="text-red-600">Error</span>
                        ) : (
                          `Created ${formatDate(flow.createdAt)}`
                        )}
                      </p>
                    </div>

                    {/* Delete button.
                        flow_snapshots has no name column, so flow.name is
                        undefined for generated flows and this previously
                        announced "Delete undefined" to screen readers. The
                        aria-label uses the same fallback as the visible label
                        rendered above. */}
                    <button
                      type="button"
                      onClick={(e) => handleDeleteClick(e, flow.id)}
                      className={cn(
                        'p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity',
                        'hover:bg-gray-200 text-gray-400 hover:text-red-600'
                      )}
                      aria-label={`Delete ${flow.name || formatFlowPeriod(flow)}`}
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 text-xs text-gray-500">
        {flows.length} flow{flows.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
